import { useEffect, useState } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import { useNetworkState } from '../../hooks/useNetworkState';
import { offlineQueueService } from '../../services/OfflineQueueService';
import api from '../../api/axios';
import { visitsApi } from '../../api/visits';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { prepareImageUpload } from '../../utils/image-compression';

export function OfflineSyncManager() {
  const { t } = useTranslation();
  const { isOnline } = useNetworkState();
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Refresh count on mount
    offlineQueueService.getQueue().then((q) => setQueueCount(q.length));
  }, []);

  async function syncQueue() {
    if (isSyncing) return;

    const queue = await offlineQueueService.getQueue();
    setQueueCount(queue.length);
    if (queue.length === 0) return;

    setIsSyncing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const action of queue) {
      try {
        if (action.type === 'CREATE_ORDER' || action.type === 'UPDATE_ORDER') {
          if (action.method === 'PUT') {
            await api.put(action.url!, action.payload);
          } else {
            await api.post(action.url!, action.payload);
          }
          await offlineQueueService.removeFromQueue(action.id);
          successCount++;
        } else if (action.type === 'UPDATE_VISIT') {
          const payload: any = { ...(action.payload as any) };

          // 1. Upload files if they exist
          if (payload.offline_files) {
            async function uploadFile(file: File) {
              const preparedFile = await prepareImageUpload(file);
              const formData = new FormData();
              formData.append('file', preparedFile);
              const res = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              return res.data.url;
            }

            if (payload.offline_files.facade && payload.checkin) {
              payload.checkin.photo_url = await uploadFile(payload.offline_files.facade);
            }
            if (payload.offline_files.shelf && payload.checkout) {
              payload.checkout.photo_url = await uploadFile(payload.offline_files.shelf);
            }
            if (payload.offline_files.equipment && payload.checkout) {
              payload.checkout.equipment_photo_url = await uploadFile(payload.offline_files.equipment);
            }
            delete payload.offline_files;
          }

          // 2. Perform Checkin
          if (payload.checkin) {
            await visitsApi.checkin(payload.visit_id, payload.checkin);
          }

          // 3. Perform Checkout
          if (payload.checkout) {
            await visitsApi.checkout(payload.visit_id, payload.checkout);
          }

          await offlineQueueService.removeFromQueue(action.id);
          successCount++;
        }
      } catch (error) {
        console.error(`Failed to sync action ${action.id}:`, error);
        errorCount++;
      }
    }

    setIsSyncing(false);
    const newQueue = await offlineQueueService.getQueue();
    setQueueCount(newQueue.length);

    if (successCount > 0) {
      toast.success(`Синхронизировано задач: ${successCount}`);
      queryClient.invalidateQueries({ queryKey: ['agent-orders'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['agent-dashboard'] });
    }
    if (errorCount > 0) {
      toast.error(`Не удалось синхронизировать ${errorCount} задач.`);
    }
  }

  useEffect(() => {
    if (isOnline) {
      syncQueue();
    }
  }, [isOnline]);

  if (!isSyncing && queueCount === 0) return null;

  return (
    <div className="fixed top-14 left-0 right-0 z-40 bg-indigo-500 text-white text-[10px] font-bold uppercase tracking-wider text-center py-1.5 shadow-xs flex items-center justify-center gap-2">
      {isSyncing ? (
        <>
          <Loader2 size={12} className="animate-spin" />
          <span>
            {t('sync.syncing')} ({queueCount})...
          </span>
        </>
      ) : (
        <span>
          {t('sync.pending')}: {queueCount}
        </span>
      )}
    </div>
  );
}
